/* global __BUILD_TAG__ */
import { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation, matchPath } from 'react-router-dom';
import {
  OFERTA_PATH,
  OPERATOR_EMAIL,
  OPERATOR_INN,
  OPERATOR_NAME,
  OPERATOR_OGRNIP,
} from '../legal/operator';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { WorkChromeProvider, useWorkChrome } from '../context/WorkChromeContext';
import { TabIdentityProvider } from '../context/TabIdentityContext';
import WorkToolbar from './WorkToolbar';
import UserAccountMenu from './UserAccountMenu';
import CookieNotice from './CookieNotice';
import api from '../api/client';
import useVisitHeartbeat from '../hooks/useVisitHeartbeat';

function showThematicMenuLinks(user) {
  if (!user) return false;
  return Boolean(
    user.is_student || user.is_teacher || user.is_staff || user.is_admin
  );
}

const BUILD_TAG = typeof __BUILD_TAG__ !== 'undefined' ? __BUILD_TAG__ : 'режим разработки (без сборки)';

function isCollabChromePath(pathname) {
  return Boolean(
    matchPath({ path: '/collaborative', end: true }, pathname) ||
      matchPath({ path: '/collaborative/:sessionId', end: true }, pathname),
  );
}

function LayoutInner() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { toolbarProps } = useWorkChrome();
  const isDark = theme === 'dark';
  const collabChrome = isCollabChromePath(location.pathname);
  const [topicMenu, setTopicMenu] = useState([]);
  const [navOpen, setNavOpen] = useState(false);
  const navRef = useRef(null);
  const menuBtnRef = useRef(null);

  useEffect(() => {
    if (collabChrome || !showThematicMenuLinks(user)) {
      setTopicMenu([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/tasks/topic-programs/menu/');
        const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
        if (!cancelled) setTopicMenu(data);
      } catch {
        if (!cancelled) setTopicMenu([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, collabChrome]);

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch {
      navigate('/login');
    }
  }

  const isTeacherCabinet = Boolean(user?.is_teacher || user?.is_staff || user?.is_admin);
  const isSiteAdmin = Boolean(user?.is_staff || user?.is_admin);
  const [newUsersUnread, setNewUsersUnread] = useState(0);

  useVisitHeartbeat(user);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return undefined;
    function onPointerDown(event) {
      const target = event.target;
      if (navRef.current?.contains(target) || menuBtnRef.current?.contains(target)) return;
      setNavOpen(false);
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') setNavOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [navOpen]);

  useEffect(() => {
    if (!isSiteAdmin || collabChrome) {
      setNewUsersUnread(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/teacher/new-users/?filter=unread&page_size=1');
        if (!cancelled) setNewUsersUnread(Number(res.data?.unread_count) || 0);
      } catch {
        if (!cancelled) setNewUsersUnread(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSiteAdmin, collabChrome, location.pathname]);

  return (
    <div className={`layout${collabChrome ? ' layout--collab-chrome' : ''}`}>
      <header className="header">
        <div className="header-inner">
          <NavLink to="/" className="logo" title="Главная">
            <span className="logo-icon">📘</span>
            <span className="logo-text">
              diobuch{collabChrome ? null : <span className="logo-version"> v2.0</span>}
            </span>
          </NavLink>

          {collabChrome ? (
            <nav className="nav nav--collab-chrome" aria-label="Быстрая навигация">
              <NavLink to="/" end className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
                Главная
              </NavLink>
              {user?.is_student && (
                <NavLink
                  to="/konspekty"
                  className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                >
                  Конспекты
                </NavLink>
              )}
              {isTeacherCabinet && (
                <NavLink
                  to="/teacher"
                  className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                >
                  Кабинет преподавателя
                </NavLink>
              )}
            </nav>
          ) : (
            <nav
              id="site-nav"
              ref={navRef}
              className={navOpen ? 'nav nav-open' : 'nav'}
              aria-label="Основная навигация"
            >
              <NavLink to="/" end className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
                Главная
              </NavLink>
              {user?.is_student && (
                <NavLink to="/tasks" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
                  Задачи
                </NavLink>
              )}
              <NavLink
                to="/practice-quizzes"
                className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
              >
                Тесты
              </NavLink>
              <NavLink
                to="/trainers"
                className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
              >
                Тренажёры
              </NavLink>
              {user && (
                <>
                  {topicMenu.map((p) => (
                    <NavLink
                      key={p.slug}
                      to={`/themes/${p.slug}`}
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      {p.label || p.slug}
                    </NavLink>
                  ))}
                  {user?.is_student && (
                    <NavLink
                      to="/my-teachers"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Мои преподаватели
                    </NavLink>
                  )}
                  {user?.is_student && (
                    <NavLink
                      to="/topic-progress"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Прогресс по темам
                    </NavLink>
                  )}
                  {user?.is_student && (
                    <NavLink
                      to="/my-attempts"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Мои попытки
                    </NavLink>
                  )}
                  {user?.is_student && (
                    <NavLink
                      to="/my-schedule"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Расписание
                    </NavLink>
                  )}
                  {user?.is_student && (
                    <NavLink
                      to="/my-lessons"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Журнал занятий
                    </NavLink>
                  )}
                  {isTeacherCabinet && (
                    <NavLink
                      to="/teacher/schedule"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Расписание
                    </NavLink>
                  )}
                  {isTeacherCabinet && (
                    <NavLink
                      to="/teacher"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Кабинет преподавателя
                    </NavLink>
                  )}
                  {user?.is_parent && (
                    <NavLink
                      to="/my-schedule"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Расписание
                    </NavLink>
                  )}
                  {user?.is_parent && (
                    <NavLink
                      to="/parent"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Дети
                    </NavLink>
                  )}
                  {user && !user.is_teacher && (
                    <NavLink
                      to="/billing"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Баланс
                    </NavLink>
                  )}
                  {user?.is_student && (
                    <NavLink
                      to="/dashboard"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Мой прогресс
                    </NavLink>
                  )}
                  <NavLink
                    to="/konspekty"
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                  >
                    Конспект
                  </NavLink>
                  <NavLink
                    to="/collaborative"
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                  >
                    Совместное кодирование
                  </NavLink>
                  {isTeacherCabinet && (
                    <NavLink
                      to="/teacher/billing"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Оплата учеников
                    </NavLink>
                  )}
                  {isTeacherCabinet && (
                    <NavLink
                      to="/teacher/tasks"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Конструктор задач
                    </NavLink>
                  )}
                  {isTeacherCabinet && (
                    <NavLink
                      to="/teacher/tags"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Теги
                    </NavLink>
                  )}
                  {isTeacherCabinet && (
                    <NavLink
                      to="/teacher/ege-map"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Карта ЕГЭ
                    </NavLink>
                  )}
                  {isSiteAdmin && (
                    <NavLink
                      to="/teacher/new-users"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Новые пользователи
                      {newUsersUnread > 0 ? ` (${newUsersUnread})` : ''}
                    </NavLink>
                  )}
                  {(user.is_staff || user.is_admin) && (
                    <NavLink
                      to="/admin/topic-programs"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Программы (темы)
                    </NavLink>
                  )}
                </>
              )}
            </nav>
          )}

          {collabChrome && toolbarProps ? (
            <WorkToolbar {...toolbarProps} className="work-toolbar--chrome" />
          ) : null}

          <div className="header-actions">
            <div className="header-theme-toggle" role="group" aria-label="Тема сайта">
              <button
                type="button"
                className={`btn btn-outline btn-xs btn-icon-only ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
                title="Светлая тема сайта"
                aria-label="Светлая тема сайта"
              >
                <i className="fa-solid fa-sun ui-icon" aria-hidden />
              </button>
              <button
                type="button"
                className={`btn btn-outline btn-xs btn-icon-only ${isDark ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
                title="Тёмная тема сайта"
                aria-label="Тёмная тема сайта"
              >
                <i className="fa-solid fa-moon ui-icon" aria-hidden />
              </button>
            </div>
            {user ? (
              <UserAccountMenu user={user} onLogout={handleLogout} />
            ) : (
              <div className="auth-links">
                <NavLink to="/login" className="btn btn-outline btn-sm">
                  Войти
                </NavLink>
                <NavLink to="/register" className="btn btn-primary btn-sm">
                  Регистрация
                </NavLink>
              </div>
            )}
          </div>

          {!collabChrome && (
            <button
              type="button"
              ref={menuBtnRef}
              className="mobile-menu-btn"
              aria-expanded={navOpen}
              aria-controls="site-nav"
              aria-label={navOpen ? 'Закрыть меню' : 'Открыть меню'}
              onClick={() => setNavOpen((open) => !open)}
            >
              ☰
            </button>
          )}
        </div>
      </header>
      <main className="main-content">
        <Outlet />
      </main>
      {!collabChrome && (
        <footer className="footer">
          <p className="footer-operator">
            Оператор ПДн: {OPERATOR_NAME}
            {' · '}ИНН {OPERATOR_INN}
            {' · '}ОГРНИП {OPERATOR_OGRNIP}
          </p>
          <p className="footer-operator">
            Запросы субъектов ПДн:{' '}
            <a href={`mailto:${OPERATOR_EMAIL}`}>{OPERATOR_EMAIL}</a>
          </p>
          <nav className="footer-links" aria-label="Документы">
            <Link to="/privacy">Политика обработки ПДн</Link>
            <Link to="/consent">Согласие на обработку ПДн</Link>
            <Link to={OFERTA_PATH}>Договор-оферта</Link>
          </nav>
          <p className="footer-cookies">
            Сайт использует необходимые cookie.
          </p>
          <p>© 2026 diobuch — подготовка к ЕГЭ/ОГЭ по информатике</p>
          <p className="footer-build">Обновлено: {BUILD_TAG}</p>
        </footer>
      )}
      <CookieNotice />
    </div>
  );
}

export default function Layout() {
  return (
    <WorkChromeProvider>
      <TabIdentityProvider>
        <LayoutInner />
      </TabIdentityProvider>
    </WorkChromeProvider>
  );
}
